// frontend/src/pages/Eval.js
import { useState, useEffect } from "react";
import api from "../api/client";
import Sidebar from "../components/Sidebar";

const ORANGE = "#D95B2A";
const ORANGE_LIGHT = "#F4EAE4";

export default function Eval() {
  const [step, setStep] = useState("select");
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState("");
  const [selectedSousTheme, setSelectedSousTheme] = useState("");
  const [nbQuestions, setNbQuestions] = useState(10);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/api/eval/themes/").then((res) => setThemes(res.data.themes)).catch(() => {});
    api.get("/api/eval/history/").then((res) => setHistory(res.data.history)).catch(() => {});
  }, []);

  const currentThemeObj = themes.find((t) => t.theme === selectedTheme);
  const sousThemes = currentThemeObj?.sous_themes || [];

  const startEval = async () => {
    if (!selectedTheme) return;
    setStep("loading");
    try {
      const params = new URLSearchParams({ n: nbQuestions });
      if (selectedSousTheme) params.append("sous_theme", selectedSousTheme);
      else params.append("theme", selectedTheme);

      const res = await api.get(`/api/eval/questions/?${params}`);
      setQuestions(res.data.questions);
      setAnswers([]);
      setCurrent(0);
      setSelected(null);
      setConfirmed(false);
      setStep("quiz");
    } catch {
      setStep("select");
      alert("Impossible de charger les questions pour ce thème.");
    }
  };

  const confirm = () => {
    if (!selected) return;
    const q = questions[current];
    setAnswers((prev) => [
      ...prev,
      { term_id: q.id, selected, correct: q.correct_answer },
    ]);
    setConfirmed(true);
  };

  const next = () => {
    if (current + 1 < questions.length) {
      setCurrent((c) => c + 1);
      setSelected(null);
      setConfirmed(false);
    } else {
      submitEval();
    }
  };

  const submitEval = async () => {
    try {
      const res = await api.post("/api/eval/submit/", {
        answers,
        theme: selectedTheme,
        sous_theme: selectedSousTheme,
      });
      setResult(res.data);
      setStep("result");
      api.get("/api/eval/history/").then((r) => setHistory(r.data.history));
    } catch {
      alert("Erreur lors de l'envoi des résultats.");
    }
  };

  const restart = () => {
    setStep("select");
    setResult(null);
    setSelectedTheme("");
    setSelectedSousTheme("");
  };

  const mentionColor = (pct) => {
    if (pct >= 90) return "#2e7d32";
    if (pct >= 75) return "#388e3c";
    if (pct >= 60) return ORANGE;
    return "#c62828";
  };

  return (
    <div className="page-container">
      <Sidebar />
      <div className="content-container">
        <h1 style={{ fontWeight: 700, fontSize: "1.6rem", marginBottom: "2rem" }}>
          Évaluation
        </h1>

      {/* ── Sélection du thème ── */}
      {step === "select" && (
        <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
          <div style={cardStyle}>
            <h2 style={{ color: ORANGE, marginBottom: "0.5rem" }}>
              Choisir un thème
            </h2>
            <p style={{ color: "#555", marginBottom: "1.5rem", lineHeight: 1.6 }}>
              Sélectionner un thème (et optionnellement un sous-thème) pour générer
              une évaluation notée. Le score sera enregistré.
            </p>

            <label style={labelStyle}>Thème *</label>
            <select
              value={selectedTheme}
              onChange={(e) => { setSelectedTheme(e.target.value); setSelectedSousTheme(""); }}
              style={selectStyle}
            >
              <option value="">— Sélectionner un thème —</option>
              {themes.map((t) => (
                <option key={t.theme} value={t.theme}>{t.theme}</option>
              ))}
            </select>

            {sousThemes.length > 0 && (
              <>
                <label style={{ ...labelStyle, marginTop: "1rem" }}>
                  Sous-thème (optionnel)
                </label>
                <select
                  value={selectedSousTheme}
                  onChange={(e) => setSelectedSousTheme(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">— Tous les sous-thèmes —</option>
                  {sousThemes.map((st) => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
              </>
            )}

            <label style={{ ...labelStyle, marginTop: "1rem" }}>
              Nombre de questions
            </label>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
              {[5, 10, 15, 20].map((n) => (
                <button
                  key={n}
                  onClick={() => setNbQuestions(n)}
                  style={{
                    padding: "0.4rem 1rem",
                    borderRadius: 8,
                    border: `2px solid ${nbQuestions === n ? ORANGE : "#ddd"}`,
                    background: nbQuestions === n ? ORANGE : "#fff",
                    color: nbQuestions === n ? "#fff" : "#333",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {n}
                </button>
              ))}
            </div>

            <button
              onClick={startEval}
              disabled={!selectedTheme}
              style={{ ...btnPrimaryStyle, opacity: selectedTheme ? 1 : 0.4 }}
            >
              Commencer l'évaluation →
            </button>
          </div>

          {history.length > 0 && (
            <div style={{ ...cardStyle, flex: "1 1 280px" }}>
              <h2 style={{ marginBottom: "1rem", fontSize: "1rem", fontWeight: 700 }}>
                Évaluations récentes
              </h2>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ color: "#888", textAlign: "left" }}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Thème</th>
                    <th style={thStyle}>%</th>
                    <th style={thStyle}>Mention</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={tdStyle}>{h.date}</td>
                      <td style={tdStyle}>{h.sous_theme || h.theme}</td>
                      <td style={tdStyle}>{h.percentage}%</td>
                      <td style={tdStyle}>
                        <span style={{ color: mentionColor(h.percentage), fontWeight: 600 }}>
                          {h.mention}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Chargement ── */}
      {step === "loading" && (
        <div style={{ textAlign: "center", marginTop: "5rem" }}>
          <div style={spinnerStyle} />
          <p style={{ color: "#888", marginTop: "1rem" }}>Génération de l'évaluation…</p>
        </div>
      )}

      {/* ── Quiz ── */}
      {step === "quiz" && questions.length > 0 && (
        <div style={{ maxWidth: 640 }}>
          <div style={{ marginBottom: "1.5rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
              <span style={{ fontWeight: 600, color: ORANGE }}>
                Question {current + 1} / {questions.length}
              </span>
              <span style={{ color: "#888", fontSize: "0.85rem" }}>
                {selectedSousTheme || selectedTheme}
              </span>
            </div>
            <div style={{ background: "#e0e0e0", borderRadius: 99, height: 6 }}>
              <div style={{
                width: `${((current + 1) / questions.length) * 100}%`,
                background: ORANGE,
                height: 6,
                borderRadius: 99,
                transition: "width 0.3s",
              }} />
            </div>
          </div>

          <div style={cardStyle}>
            <p style={{ color: "#888", fontSize: "0.85rem", marginBottom: "0.3rem" }}>
              Traduction de :
            </p>
            <h2 style={{ fontSize: "1.8rem", fontWeight: 700, marginBottom: "0.5rem" }}>
              {questions[current].term}
            </h2>
            {questions[current].example_sentence && (
              <p style={{
                color: "#666", fontSize: "0.9rem", fontStyle: "italic",
                marginBottom: "1.5rem", lineHeight: 1.5,
              }}>
                "{questions[current].example_sentence}"
              </p>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {questions[current].choices.map((choice, i) => {
                let bg = "#fff", border = "2px solid #e0e0e0", color = "#333";
                if (confirmed) {
                  if (choice === questions[current].correct_answer) {
                    bg = "#e8f5e9"; border = "2px solid #4caf50"; color = "#2e7d32";
                  } else if (choice === selected) {
                    bg = "#fdecea"; border = "2px solid #f44336"; color = "#c62828";
                  }
                } else if (choice === selected) {
                  bg = ORANGE_LIGHT; border = `2px solid ${ORANGE}`;
                }
                return (
                  <button
                    key={i}
                    onClick={() => !confirmed && setSelected(choice)}
                    style={{
                      padding: "0.8rem 1rem", borderRadius: 10,
                      border, background: bg, color,
                      textAlign: "left",
                      cursor: confirmed ? "default" : "pointer",
                      fontWeight: choice === selected ? 600 : 400,
                      fontSize: "0.95rem", transition: "all 0.15s",
                    }}
                  >
                    {choice}
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: "1.5rem" }}>
              {!confirmed ? (
                <button onClick={confirm} disabled={!selected}
                  style={{ ...btnPrimaryStyle, opacity: selected ? 1 : 0.4 }}>
                  Valider
                </button>
              ) : (
                <button onClick={next} style={btnPrimaryStyle}>
                  {current + 1 < questions.length ? "Suivant →" : "Voir le résultat →"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Résultats ── */}
      {step === "result" && result && (
        <div style={{ maxWidth: 480 }}>
          <div style={{ ...cardStyle, textAlign: "center" }}>
            <div style={{
              width: 120, height: 120, borderRadius: "50%",
              background: ORANGE_LIGHT, margin: "0 auto 1rem",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ fontSize: "2rem", fontWeight: 800, color: ORANGE }}>
                {result.percentage}%
              </span>
            </div>
            <h2 style={{ fontSize: "1.4rem", fontWeight: 700, marginBottom: "0.3rem" }}>
              {result.score} / {result.total}
            </h2>
            <div style={{
              display: "inline-block",
              background: mentionColor(result.percentage),
              color: "#fff", borderRadius: 20,
              padding: "0.3rem 1rem", fontWeight: 700, marginBottom: "1rem",
            }}>
              {result.mention}
            </div>
            <p style={{ color: "#555", marginBottom: "0.5rem" }}>
              Thème : <strong>{selectedSousTheme || selectedTheme}</strong>
            </p>
            <p style={{ color: "#888", marginBottom: "2rem", fontSize: "0.9rem" }}>
              Ce résultat a été enregistré dans les statistiques.
            </p>
            <button onClick={restart} style={btnPrimaryStyle}>
              Nouvelle évaluation
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

const cardStyle = {
  background: "#fff", borderRadius: 16, padding: "1.5rem",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)", flex: "1 1 320px",
};
const btnPrimaryStyle = {
  background: ORANGE, color: "#fff", border: "none", borderRadius: 10,
  padding: "0.75rem 1.5rem", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
};
const labelStyle = { fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.4rem", display: "block" };
const selectStyle = {
  width: "100%", padding: "0.6rem 0.8rem", borderRadius: 8,
  border: "2px solid #e0e0e0", fontSize: "0.95rem", marginBottom: "0.5rem",
  outline: "none", background: "#fff",
};
const thStyle = { padding: "0.4rem 0.5rem", fontWeight: 600, fontSize: "0.8rem" };
const tdStyle = { padding: "0.5rem" };
const spinnerStyle = {
  width: 40, height: 40, border: "4px solid #eee",
  borderTop: `4px solid ${ORANGE}`, borderRadius: "50%",
  margin: "0 auto",
};